import { Box, Button, LinearProgress, Paper, Slide, Typography } from '@mui/material'
import { FC, useCallback } from 'react'

import { useNavigate } from 'react-router-dom'
import { isGuest } from '../../../../helpers'
import { Dialogs, useDialogs } from '../../../../sharedComponents/src/Contexts/DialogsContext'
import { Countdown } from '../../../../sharedComponents/src/globalHeader/components/Countdown'
import { getAppConfig } from '../../../../sharedComponents/src/globalHeader/services/AppConfigService'
import { useScreenSize } from '../../../../sharedComponents/src/hooks/useScreenSize'
import { useStoreActions, useStoreState } from '../../../../store/hooks'
import { Mission } from '../../../MissionService/missionsTypes'
import { AppStorePopup } from './AppStorePopup'
import { MissionIcon } from './MissionIcon'

const normalise = (value, threshold) => {
    const normalisedValue = (value * 100) / threshold

    return Math.min(100, normalisedValue)
}

export const MissionCard: FC<Mission> = (mission) => {
    const {
        title,
        threshold,
        progress,
        tooltip,
        description,
        assetUrl,
        endDate,
        resetDate,
        featurePath,
        multiplier,
        multiplierExpiryDate,
    } = mission
    const navigate = useNavigate()
    const loadingSummary = useStoreState((state) => state.missionsService.loadingSummary)
    const { claimMission } = useStoreActions((state) => state.missionsService)
    const { getMissions } = useStoreActions((state) => state.missionsService)
    const { guestEnabled } = useStoreState((state) => state.missionsService)
    const userData = useStoreState((state) => state.userData)
    const { registerDialog, unregisterDialog } = useDialogs()
    const { isMobile } = useScreenSize()

    const guest = isGuest(userData.userData?.groups)

    const isClaimable = progress >= threshold

    const handleNavigate = useCallback(() => {
        if (featurePath) {
            switch (featurePath) {
                case '/application-store-popup':
                    if (getAppConfig()?.isMobileApp) {
                        // INVALID SCENARIO
                        // logoutHandler()
                    } else {
                        registerDialog(
                            Dialogs.MISSIONS_APP_STORE,
                            <AppStorePopup
                                onClose={() => {
                                    unregisterDialog(Dialogs.MISSIONS_APP_STORE)
                                }}
                            />,
                        )
                    }
                    break
                default:
                    navigate(featurePath)
            }
        } else {
            alert(`[featurePath] key is missing for challenge ${mission.id}.`)
        }
    }, [featurePath, mission.id, navigate])

    const handleClaim = useCallback(async () => {
        if (!guest || guestEnabled) {
            claimMission(mission)
        }
    }, [mission, guest, guestEnabled])

    return (
        <>
            <Slide direction="right" in>
                <Paper
                    id={`challenge-${mission.id}`}
                    sx={{
                        backgroundColor: 'transparent',
                        borderColor: progress ? (isClaimable ? 'secondary.main' : 'primary.main') : 'common.white',
                        pb: 1,
                        px: 1,
                        borderRadius: 0,
                        borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
                        position: 'relative',
                    }}
                >
                    {loadingSummary && (
                        <LinearProgress
                            sx={{
                                opacity: '0.5',
                                position: 'absolute',
                                right: 0,
                                left: 0,
                                bottom: -2,
                                width: '100%',
                                zIndex: '100',
                                height: '1px',
                            }}
                            variant="indeterminate"
                        />
                    )}
                    <Box
                        sx={{
                            display: 'grid',
                            width: '100%',
                            background: 'none',
                            gap: 1,
                            gridTemplateRows: '1fr',
                            gridTemplateColumns: 'auto 1fr',
                            backgroundColor: 'transparent',
                            gridTemplateAreas: `"icon title title" 
                                                "icon status status"
                                                "progress progress button"`,
                        }}
                    >
                        <Box
                            sx={{
                                gridArea: 'title',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                flexGrow: 1,
                                gap: 1,
                            }}
                        >
                            <Box>
                                <Typography variant={isMobile ? 'h6' : 'h5'} textTransform="uppercase">
                                    {title}
                                </Typography>
                                <Typography className="description" variant="body2" color="text.disabled">
                                    {description}
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                gridArea: 'status',
                            }}
                        >
                            <Countdown resetDate={resetDate} />
                        </Box>

                        <Box
                            sx={{
                                gridArea: 'progress',
                                position: 'relative',
                                alignSelf: 'center',
                                border:
                                    multiplier > 1 ? (theme) => `2px solid ${theme.palette.secondary.main}` : 'none',
                                pl: multiplier > 1 ? '26px' : 0,

                                '@keyframes glowPulse': {
                                    '0%': {
                                        boxShadow: 'none',
                                    },
                                    '25%': {
                                        boxShadow: (theme) => `0 0 10px ${theme.palette.secondary.main}`,
                                    },
                                    '50%': {
                                        boxShadow: 'none',
                                    },
                                    '100%': {
                                        boxShadow: 'none',
                                    },
                                },
                                animation: multiplier > 1 ? 'glowPulse 3s ease-in-out infinite' : 'none',
                            }}
                        >
                            {multiplier > 1 && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -2,
                                        left: -3,
                                        bottom: -2,
                                        zIndex: 1,
                                        backgroundColor: 'secondary.main',
                                        display: 'flex',
                                        width: '36px',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        outline: (theme) => `2px solid ${theme.palette.background.default}`,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        textAlign="center"
                                        sx={{
                                            color: '#000',
                                            fontSize: '18px',
                                            fontStyle: 'normal',
                                            fontWeight: 700,
                                            lineHeight: '24px',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {multiplier}X
                                    </Typography>
                                </Box>
                            )}
                            {multiplier > 1 && multiplierExpiryDate && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: -18,
                                        left: -3,
                                        right: 0,
                                        color: 'secondary.main',
                                        width: '36px',
                                        textAlign: 'center',
                                        '> p': {
                                            fontSize: '10px !important',
                                        },
                                    }}
                                >
                                    <Countdown resetDate={multiplierExpiryDate} loader={false} callback={getMissions} />
                                </Box>
                            )}
                            <LinearProgress
                                size="small"
                                variant="determinate"
                                color={isClaimable ? 'secondary' : 'primary'}
                                value={normalise(progress, threshold)}
                                meter
                                animated={mission.multiplier > 1}
                            />
                        </Box>

                        <MissionIcon mission={mission} isClaimable={isClaimable} />

                        <Box sx={{ gridArea: 'button', display: 'flex', flexDirection: 'row', gap: 1 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    minWidth: '64px',
                                    textAlign: 'center',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontWeight: 'bold',
                                }}
                            >
                                {progress}/{threshold}
                            </Typography>
                            <Button
                                onClick={() => {
                                    if (isClaimable) {
                                        handleClaim()
                                    } else {
                                        handleNavigate()
                                    }
                                }}
                                size="medium"
                                variant="contained"
                                color={isClaimable ? 'secondary' : 'primary'}
                                fullWidth
                                sx={{ minWidth: '80px', padding: 0 }}
                            >
                                {isClaimable ? 'Claim' : 'Go'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Slide>
        </>
    )
}
